import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, CheckCircle, Clock, Monitor, Rocket, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import useScrollToTop from '../hooks/useScrollToTop';

function Roadmap() {
  useScrollToTop();

  const roadmapItems = [
    {
      phase: "Phase 1",
      title: "macOS App Launch",
      description: "The revolutionary ClaraAI productivity app makes its debut on macOS, bringing AI-powered Vision Frame technology to Mac users worldwide.",
      status: "In Development",
      icon: <Monitor className="w-8 h-8 text-clara-blue-600" />,
      features: [
        "AI-powered Vision Frame with transparent overlay",
        "Multi-AI integration (OpenAI, Gemini, Grok, Ollama)",
        "Real-time audio transcription and analysis",
        "Smart automation with AppleScript/JavaScript",
        "Privacy-first local processing",
        "Global keyboard shortcuts (⌘A, ⌘Q, ⌘L)",
        "Seamless integration with macOS design language"
      ],
      date: "TBD",
      color: "from-clara-blue-500 to-clara-blue-600",
      bgColor: "bg-clara-blue-50",
      borderColor: "border-clara-blue-200"
    },
    {
      phase: "Phase 2", 
      title: "Windows App Launch",
      description: "Expanding ClaraAI's revolutionary productivity capabilities to Windows users, bringing the same powerful AI-driven workflow automation to the world's most popular desktop platform.",
      status: "Planned",
      icon: <Target className="w-8 h-8 text-purple-600" />,
      features: [
        "Full Windows 11/10 compatibility",
        "Adapted Vision Frame for Windows UI",
        "Windows-specific automation capabilities",
        "Enhanced multi-monitor support",
        "Windows Store distribution",
        "Enterprise deployment options",
        "Cross-platform sync capabilities"
      ],
      date: "TBD",
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200"
    }
  ];



  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>ClaraAI Roadmap — What’s Coming Next</title>
        <meta name="description" content="See the ClaraAI roadmap: macOS launch, Windows support, Vision Frame enhancements, and more." />
        <link rel="canonical" href="https://claraai.app/roadmap" />
      </Helmet>
      <Header />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-20">
        {/* Dynamic Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900"></div>
        
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
                              radial-gradient(circle at 75% 75%, rgba(147, 51, 234, 0.3) 0%, transparent 50%)`,
            backgroundSize: '100% 100%'
          }}></div>
        </div>
        
        {/* Floating UI Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-1/3 w-24 h-24 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Back Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <Link
                to="/"
                className="inline-flex items-center text-white hover:text-blue-300 transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </Link>
            </motion.div>

            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-6"
            >
              <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white font-medium text-sm">
                <Rocket className="w-4 h-4 mr-2 text-blue-300" />
                Product Roadmap
              </div>
            </motion.div>
            
            {/* Main Headline */}
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
            >
              ClaraAI <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">Roadmap</span>
            </motion.h1>
            
            {/* Subtitle */}
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed"
            >
              Our journey to revolutionize productivity across all platforms. From macOS to Windows, we're building the future of AI-powered workflow automation.
            </motion.p>
          </div>
        </div>
      </section>



      {/* Roadmap Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-clara-gray-900 mb-6">
              Our <span className="gradient-text">Development Journey</span>
            </h2>
            <p className="text-xl text-clara-gray-600 max-w-3xl mx-auto">
              We're committed to bringing ClaraAI's revolutionary productivity features to users across all major platforms.
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            {roadmapItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 * index }}
                className={`mb-16 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} lg:flex items-center gap-12`}
              >
                {/* Content */}
                <div className="flex-1 lg:order-1">
                  <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold mb-4 ${item.bgColor} ${item.borderColor} border`}>
                    <span className={`bg-gradient-to-r ${item.color} bg-clip-text text-transparent`}>
                      {item.phase}
                    </span>
                  </div>
                  
                  <h3 className="text-3xl md:text-4xl font-bold text-clara-gray-900 mb-4">
                    {item.title}
                  </h3>
                  
                  <p className="text-lg text-clara-gray-600 mb-6 leading-relaxed">
                    {item.description}
                  </p>
                  
                  {/* Status Badge */}
                  <div className="flex items-center mb-6">
                    {item.status === "In Development" ? (
                      <div className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                        <Clock className="w-4 h-4 mr-2" />
                        {item.status}
                      </div>
                    ) : (
                      <div className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                        <Calendar className="w-4 h-4 mr-2" />
                        {item.status}
                      </div>
                    )}
                    <span className="ml-3 text-clara-gray-500 font-medium">
                      Launch: {item.date}
                    </span>
                  </div>
                  
                  {/* Features List */}
                  <div className="space-y-3">
                    {item.features.map((feature, featureIndex) => (
                      <motion.div
                        key={featureIndex}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 * featureIndex }}
                        className="flex items-start space-x-3"
                      >
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-clara-gray-700">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
                
                {/* Visual Element */}
                <div className="flex-1 lg:order-2 flex justify-center">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className={`relative p-8 rounded-3xl ${item.bgColor} border ${item.borderColor} shadow-xl`}
                  >
                    <div className="w-24 h-24 bg-gradient-to-br from-white to-gray-100 rounded-2xl flex items-center justify-center shadow-lg mb-6">
                      {item.icon}
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent mb-2`}>
                        {item.phase}
                      </div>
                      <div className="text-clara-gray-600 font-medium">
                        {item.status}
                      </div>
                    </div>
                    
                    {/* Decorative Elements */}
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full animate-pulse"></div>
                    <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-clara-blue-600 to-clara-blue-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Be Part of the Journey
            </h2>
            <p className="text-xl text-clara-blue-100 mb-8 max-w-2xl mx-auto">
              Join our waitlist to be among the first to experience ClaraAI when we launch. Get early access and exclusive updates.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('email-signup').scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center bg-white text-clara-blue-600 font-semibold py-4 px-8 rounded-xl hover:bg-clara-blue-50 transition-all duration-300 shadow-lg"
            >
              Join the Waitlist
            </motion.button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Roadmap; 